<template>
  <div class="class-reports">
    <div class="md-layout">
      <div class="md-layout-item md-size-100">
        <md-card>
          <md-card-header class="md-card-header-icon md-card-header-rose">
            <div class="card-icon">
              <md-icon>analytics</md-icon>
            </div>
            <h4 class="title">{{ $t('classReportsTitle') }}</h4>
            <p class="category text-secondary">{{ $t('classReportsSubtitle') }}</p>
          </md-card-header>
          <md-card-content>
            <md-field class="period-select">
              <label>{{ $t('reportPeriod') }}</label>
              <md-select v-model="period" @md-selected="loadData">
                <md-option value="week">{{ $t('periodWeek') }}</md-option>
                <md-option value="month">{{ $t('periodMonth') }}</md-option>
                <md-option value="year">{{ $t('periodYear') }}</md-option>
                <md-option value="all">{{ $t('periodAll') }}</md-option>
              </md-select>
            </md-field>
          </md-card-content>
        </md-card>
      </div>
    </div>

    <div v-if="isLoading" class="text-center p-5">
      <md-progress-spinner :md-diameter="60" md-mode="indeterminate" />
    </div>

    <template v-else-if="report">
      <div class="md-layout">
        <div class="md-layout-item md-medium-size-25 md-size-25 md-small-size-50">
          <stats-card header-color="rose">
            <template slot="header">
              <div class="card-icon"><md-icon>groups</md-icon></div>
              <p class="category">{{ $t('totalEnrollments') }}</p>
              <h3 class="title">{{ report.summary.totalEnrollments }}</h3>
            </template>
          </stats-card>
        </div>
        <div class="md-layout-item md-medium-size-25 md-size-25 md-small-size-50">
          <stats-card header-color="info">
            <template slot="header">
              <div class="card-icon"><md-icon>person</md-icon></div>
              <p class="category">{{ $t('uniqueStudents') }}</p>
              <h3 class="title">{{ report.summary.uniqueStudents }}</h3>
            </template>
          </stats-card>
        </div>
        <div class="md-layout-item md-medium-size-25 md-size-25 md-small-size-50">
          <stats-card header-color="success">
            <template slot="header">
              <div class="card-icon"><md-icon>fitness_center</md-icon></div>
              <p class="category">{{ $t('classesWithEnrollments') }}</p>
              <h3 class="title">{{ report.summary.activitiesWithEnrollments }}</h3>
            </template>
          </stats-card>
        </div>
        <div class="md-layout-item md-medium-size-25 md-size-25 md-small-size-50">
          <stats-card header-color="warning">
            <template slot="header">
              <div class="card-icon"><md-icon>trending_up</md-icon></div>
              <p class="category">{{ $t('avgEnrollmentsPerClass') }}</p>
              <h3 class="title">{{ report.summary.averagePerClass }}</h3>
            </template>
          </stats-card>
        </div>
      </div>

      <div class="md-layout" v-if="hasInsights">
        <div class="md-layout-item md-size-100">
          <div class="insights-banner p-3">
            <md-icon class="text-primary">lightbulb</md-icon>
            <span v-if="report.insights.peakClassName">
              <strong>{{ $t('mostPopularClass') }}:</strong>
              {{ report.insights.peakClassName }} ({{ report.insights.peakClassEnrollments }} {{ $t('enrollments') }})
            </span>
            <span v-if="report.insights.mostPopularDay" class="ml-3">
              <strong>{{ $t('mostPopularDay') }}:</strong>
              {{ report.insights.mostPopularDay }} ({{ report.insights.mostPopularDayEnrollments }} {{ $t('enrollments') }})
            </span>
            <span v-if="report.summary.classesAtCapacity > 0" class="ml-3">
              <strong>{{ $t('classesAtCapacity') }}:</strong> {{ report.summary.classesAtCapacity }}
            </span>
          </div>
        </div>
      </div>

      <div class="md-layout">
        <div class="md-layout-item md-size-50 md-medium-size-100">
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-rose">
              <div class="card-icon"><md-icon>leaderboard</md-icon></div>
              <h4 class="title">{{ $t('topClassesByEnrollment') }}</h4>
            </md-card-header>
            <md-card-content>
              <GChart
                v-if="topClassesChart.data.length > 1"
                type="BarChart"
                :data="topClassesChart.data"
                :options="topClassesChart.options"
              />
              <p v-else class="text-secondary p-4 text-center">{{ $t('nothingtosee') }}</p>
            </md-card-content>
          </md-card>
        </div>
        <div class="md-layout-item md-size-50 md-medium-size-100">
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-rose">
              <div class="card-icon"><md-icon>date_range</md-icon></div>
              <h4 class="title">{{ $t('enrollmentsByDayOfWeek') }}</h4>
            </md-card-header>
            <md-card-content>
              <GChart
                v-if="dayChart.data.length > 1"
                type="ColumnChart"
                :data="dayChart.data"
                :options="dayChart.options"
              />
              <p v-else class="text-secondary p-4 text-center">{{ $t('nothingtosee') }}</p>
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div class="md-layout" v-if="trendChart.data.length > 1">
        <div class="md-layout-item md-size-100">
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-rose">
              <div class="card-icon"><md-icon>show_chart</md-icon></div>
              <h4 class="title">{{ $t('enrollmentsTrend30Days') }}</h4>
            </md-card-header>
            <md-card-content>
              <GChart type="LineChart" :data="trendChart.data" :options="trendChart.options" />
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div class="md-layout">
        <div class="md-layout-item md-size-50 md-medium-size-100">
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-rose">
              <div class="card-icon"><md-icon>event</md-icon></div>
              <h4 class="title">{{ $t('topClassesTable') }}</h4>
            </md-card-header>
            <md-card-content class="table-scroll">
              <md-table v-if="report.topClasses.length">
                <md-table-row>
                  <md-table-head>#</md-table-head>
                  <md-table-head>{{ $t('name') }}</md-table-head>
                  <md-table-head>{{ $t('date') }}</md-table-head>
                  <md-table-head>{{ $t('enrollments') }}</md-table-head>
                  <md-table-head>{{ $t('quota') }}</md-table-head>
                </md-table-row>
                <md-table-row v-for="(item, index) in report.topClasses" :key="item._id">
                  <md-table-cell>{{ index + 1 }}</md-table-cell>
                  <md-table-cell>{{ item.name }}</md-table-cell>
                  <md-table-cell>
                    <span v-if="item.startDate">{{ formatClassLabel(item) }}</span>
                    <span v-else>{{ item.dayLabel }} {{ item.startTime }}</span>
                  </md-table-cell>
                  <md-table-cell>
                    <strong>{{ item.enrollments }}</strong>
                    <small v-if="item.fillRate != null" class="text-secondary"> ({{ item.fillRate }}%)</small>
                  </md-table-cell>
                  <md-table-cell>
                    <span v-if="item.cupos < 0">{{ $t('unlimited') }}</span>
                    <span v-else>{{ item.enrollments }} / {{ item.cupos }}</span>
                  </md-table-cell>
                </md-table-row>
              </md-table>
              <p v-else class="text-secondary p-4">{{ $t('nothingtosee') }}</p>
            </md-card-content>
          </md-card>
        </div>
        <div class="md-layout-item md-size-50 md-medium-size-100">
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-rose">
              <div class="card-icon"><md-icon>emoji_events</md-icon></div>
              <h4 class="title">{{ $t('topStudentsByEnrollment') }}</h4>
            </md-card-header>
            <md-card-content>
              <div
                v-for="(item, index) in report.topStudents"
                :key="item._id"
                class="customborder my-2 p-2 d-flex align-items-center"
              >
                <span class="text-primary mr-2">{{ index + 1 }}.</span>
                <avatar :logo="item.avatar" :small="true" class="mr-2" />
                <div>
                  <strong>{{ item.name }}</strong>
                  <small class="d-block text-secondary">{{ item.enrollments }} {{ $t('classesEnrolled') }}</small>
                </div>
              </div>
              <p v-if="!report.topStudents.length" class="text-secondary p-4">{{ $t('nothingtosee') }}</p>
            </md-card-content>
          </md-card>
        </div>
      </div>

      <div class="md-layout" v-if="report.upcomingPopular && report.upcomingPopular.length">
        <div class="md-layout-item md-size-100">
          <md-card>
            <md-card-header class="md-card-header-icon md-card-header-rose">
              <div class="card-icon"><md-icon>upcoming</md-icon></div>
              <h4 class="title">{{ $t('upcomingPopularClasses') }}</h4>
            </md-card-header>
            <md-card-content>
              <div class="md-layout">
                <div
                  v-for="item in report.upcomingPopular"
                  :key="'up-' + item._id"
                  class="md-layout-item md-size-33 md-medium-size-50 md-small-size-100"
                >
                  <div class="upcoming-card p-3">
                    <strong>{{ item.name }}</strong>
                    <small class="d-block text-secondary">{{ formatClassLabel(item) }}</small>
                    <span class="badge badge-primary mt-1">{{ item.enrollments }} {{ $t('enrolledPeople') }}</span>
                  </div>
                </div>
              </div>
            </md-card-content>
          </md-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { GChart } from 'vue-google-charts';
import { getClassDashboardAction } from '@/pages/Statistics/actions';
import { StatsCard, Avatar } from '@/components';

export default {
  name: 'ClassReports',
  components: { GChart, StatsCard, Avatar },
  data() {
    return {
      period: 'month',
      isLoading: false,
      report: null,
      topClassesChart: { data: [], options: {} },
      dayChart: { data: [], options: {} },
      trendChart: { data: [], options: {} },
    };
  },
  computed: {
    hasInsights() {
      return (
        this.report &&
        (this.report.insights.peakClassName ||
          this.report.insights.mostPopularDay ||
          this.report.summary.classesAtCapacity > 0)
      );
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      try {
        const data = await getClassDashboardAction(this.period);
        if (data.ok) {
          this.report = data;
          this.buildCharts();
        }
      } catch (e) {
        console.error(e);
      }
      this.isLoading = false;
    },
    buildCharts() {
      const top = this.report.topClasses.slice(0, 10);
      this.topClassesChart = {
        data: [
          [this.$t('class'), this.$t('enrollments')],
          ...top.map((c) => [
            c.name.length > 22 ? `${c.name.slice(0, 22)}…` : c.name,
            c.enrollments,
          ]),
        ],
        options: {
          legend: { position: 'none' },
          chartArea: { width: '70%', height: '75%' },
          colors: ['#e91e63'],
        },
      };

      const days = this.report.byDayOfWeek;
      this.dayChart = {
        data: [
          [this.$t('day'), this.$t('enrollments')],
          ...days.map((d) => [d.label, d.enrollments]),
        ],
        options: {
          legend: { position: 'none' },
          colors: ['#00bcd4'],
        },
      };

      const trend = this.report.enrollmentsTrend || [];
      this.trendChart = {
        data: [
          [this.$t('date'), this.$t('enrollments')],
          ...trend.map((t) => [t.date, t.enrollments]),
        ],
        options: {
          legend: { position: 'none' },
          curveType: 'function',
          colors: ['#4caf50'],
        },
      };
    },
    formatClassLabel(item) {
      if (!item.startDate) {
        return `${item.dayLabel} ${item.startTime || ''}hs`;
      }
      const d = new Date(item.startDate);
      return `${format(d, 'd/MM/yyyy')} ${item.startTime || ''}hs`;
    },
  },
};
</script>

<style scoped>
.period-select {
  max-width: 280px;
}
.insights-banner {
  background: #f5f8fa;
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.table-scroll {
  overflow-x: auto;
}
.upcoming-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  height: 100%;
}
</style>
